import React, { useEffect, useMemo, useState } from "react";

export const Spinner = ({ size = 14, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="animate-spin flex-shrink-0">
    <circle cx="12" cy="12" r="9" stroke={color} strokeOpacity="0.25" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke={color} strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const FolderGlyph = ({ system }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill={system ? "#F7E9F5" : "none"}
    stroke={system ? "#A04A97" : "#52525B"}
    strokeWidth="1.7"
    strokeLinejoin="round"
    className="flex-shrink-0"
  >
    <path d="M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

const SectionLabel = ({ children }) => (
  <p className="m-0 px-2 pt-3 pb-1 text-[12px] font-semibold tracking-[0.8px] uppercase text-[#A04A97]">
    {children}
  </p>
);

const FolderPickerModal = ({ docs, folders, currentFolder, onPick, onCreateFolder, onClose }) => {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && !creating && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saving, creating, onClose]);

  // A document lives in one folder, so the only folder you can't pick is the one it's already in.
  const blockedReason = (folder) => {
    if (currentFolder && folder.id === currentFolder.id) return "Current folder";
    const onlyHere = (d) => (d.folderIds || []).length === 1 && d.folderIds[0] === folder.id;
    return docs.every(onlyHere) ? "Current folder" : "";
  };

  const matches = (f) => f.name.toLowerCase().includes(query.trim().toLowerCase());
  const suggested = useMemo(() => folders.filter((f) => f.is_default && matches(f)), [folders, query]); // eslint-disable-line react-hooks/exhaustive-deps
  const own = useMemo(() => folders.filter((f) => !f.is_default && matches(f)), [folders, query]); // eslint-disable-line react-hooks/exhaustive-deps

  const what = docs.length === 1 ? `“${docs[0].name}”` : `${docs.length} documents`;
  const title = "Move to Folder";
  const filed = docs.some((d) => (d.folderIds || []).length > 0);
  const helper = currentFolder
    ? `${what} will leave ${currentFolder.name}.`
    : filed
    ? `${what} will leave the folder ${docs.length === 1 ? "it's" : "they're"} in now.`
    : `${what} will go into the folder you pick.`;

  const confirm = async () => {
    const folder = folders.find((f) => f.id === selectedId);
    if (!folder || saving) return;
    setSaving(true);
    const ok = await onPick(folder);
    if (!ok) setSaving(false);
  };

  const createFolder = async () => {
    const name = (newName || "").trim();
    if (!name || creating) return;
    setCreating(true);
    const folder = await onCreateFolder(name);
    setCreating(false);
    if (folder) {
      setNewName(null);
      setQuery("");
      setSelectedId(folder.id);
    }
  };

  const renderRow = (folder) => {
    const blocked = blockedReason(folder);
    const active = selectedId === folder.id;
    return (
      <button
        type="button"
        key={folder.id}
        disabled={!!blocked || saving}
        onClick={() => setSelectedId(folder.id)}
        title={folder.name}
        className={`w-full flex items-center gap-2 px-2 py-[7px] rounded-md text-left border ${
          active ? "border-[#A04A97] bg-[#FBF3FA]" : "border-transparent hover:bg-[#F1EAF4]"
        } ${blocked ? "cursor-not-allowed opacity-60 hover:bg-transparent" : "cursor-pointer"}`}
      >
        <FolderGlyph system={folder.is_default} />
        <span className="flex-1 min-w-0 truncate text-[14px] text-[#18181B]">{folder.name}</span>
        {blocked && <span className="flex-shrink-0 text-[12px] text-[#71717A]">{blocked}</span>}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-[rgba(24,16,28,0.45)] flex items-center justify-center">
      <div className="w-[480px] max-w-[92vw] bg-white rounded-xl p-6 shadow-[0_24px_48px_rgba(24,16,28,0.3)] flex flex-col gap-4 font-outfit">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1 min-w-0">
            <p className="m-0 text-[18px] font-semibold text-[#18181B]">{title}</p>
            <p className="m-0 text-[14px] leading-[1.5] text-[#3F3F46] break-words">{helper}</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={saving}
            className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-md text-[#71717A] hover:bg-[#F4F4F5]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search folders"
          className="h-9 px-3 border border-[#D4D4D8] rounded-md text-[14px] text-[#18181B] outline-none focus:border-[#A04A97] focus:shadow-[0_0_0_2px_#F3D9EF]"
        />

        <div className="max-h-[300px] overflow-y-auto custom-scrollbar -mx-1 px-1 border border-[#E4E4E7] rounded-lg py-1">
          {suggested.length > 0 && <SectionLabel>Suggested folders</SectionLabel>}
          {suggested.map(renderRow)}
          {own.length > 0 && <SectionLabel>Your folders</SectionLabel>}
          {own.map(renderRow)}
          {!suggested.length && !own.length && (
            <p className="m-0 px-2 py-4 text-[14px] text-[#71717A] text-center">No folders match “{query}”.</p>
          )}

          <div className="mt-1 pt-1 border-t border-[#F4F4F5]">
            {newName === null ? (
              <button
                type="button"
                onClick={() => setNewName("")}
                disabled={saving}
                className="w-full flex items-center gap-2 px-2 py-[7px] rounded-md text-[14px] font-medium text-[#A04A97] hover:bg-[#F1EAF4]"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                New folder
              </button>
            ) : creating ? (
              <div className="flex items-center gap-2 px-2 py-[7px] text-[14px] text-[#71717A]">
                <Spinner color="#A04A97" />
                <span className="truncate">Creating “{newName.trim()}”...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-2 py-[5px]">
                <FolderGlyph />
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") createFolder();
                    if (e.key === "Escape") {
                      e.stopPropagation();
                      setNewName(null);
                    }
                  }}
                  placeholder="Folder name"
                  className="flex-1 min-w-0 h-[28px] px-2 border border-[#A04A97] rounded text-[14px] text-[#18181B] outline-none shadow-[0_0_0_2px_#F3D9EF]"
                />
                <button
                  type="button"
                  onClick={createFolder}
                  disabled={!newName.trim()}
                  className="px-3 h-[28px] rounded-md bg-[#A04A97] text-white text-[13px] font-medium disabled:opacity-40"
                >
                  Create
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-[10px]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-[#A1A1AA] rounded-md bg-white text-[#27272A] text-[14px] font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!selectedId || saving}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#A04A97] rounded-md bg-[#A04A97] hover:bg-[#8E3F86] text-white text-[14px] font-medium disabled:opacity-50"
          >
            {saving && <Spinner />}
            {saving ? "Moving..." : "Move here"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FolderPickerModal;
